import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import BootstrapGate from "./BootstrapGate";
import ExecutionShellGate from "../features/workoutLog/recovery/ExecutionShellGate";
import { TrainIdentityProvider } from "../features/workoutLog/recovery/trainIdentity";

import HomeDashboard from "../pages/HomeDashboard";
import Program from "../pages/Program";
import AllPrograms from "../pages/AllPrograms";
import AllCycles from "../pages/AllCycles";
import ProgramDetails from "../pages/ProgramDetails";
import CycleProgramDetails from "../pages/CycleProgramDetails";
import ManualNewProgram from "../pages/ManualNewProgram";
import ManualBuilder from "../pages/ManualBuilder";
import ManualConvert from "../pages/ManualConvert";
import ManualWorkoutEditor from "../pages/ManualWorkoutEditor";
import ManualBuilderMulti from "../pages/ManualBuilderMulti";

import Train from "../pages/Train";
import Progress from "../pages/Progress";
import AICoach from "../pages/AICoach";
import AIWeeklyPlanBuilder from "../pages/AIWeeklyPlanBuilder";
import { getAIWeeklyPlanBuilderPath } from "../features/weeklyPlans/routes";
import OnboardingGate from "../features/onboarding/OnboardingGate";
import OnboardingPage from "../features/onboarding/OnboardingPage";
import AuthReturnScreen from "../auth/screens/AuthReturnScreen";
import AccountGateScreen from "../auth/screens/AccountGateScreen";
import EmailEntryScreen from "../auth/screens/EmailEntryScreen";
import OtpVerifyScreen from "../auth/screens/OtpVerifyScreen";
import WelcomeBackScreen from "../auth/screens/WelcomeBackScreen";
import useAuthBootstrap from "../auth/useAuthBootstrap";
import { resolveAnonymousEntry } from "../features/onboarding/anonymousEntry";
import { loadAnonymousOnboardingDraft } from "../features/onboarding/onboardingDraftStore";

function UnknownRouteRedirect({ bootstrap }) {
  if (bootstrap.status === "anonymous") {
    const entry = resolveAnonymousEntry({ draft: loadAnonymousOnboardingDraft() });
    return <Navigate to={entry.destination} replace />;
  }
  return <Navigate to="/" replace />;
}

export default function AppRouter({ recoveryProbe = null } = {}) {
  const bootstrap = useAuthBootstrap();

  // `/train/*` has its own gate so a locally recoverable workout survives a
  // resolving, unavailable or anonymous bootstrap (§9.8). Every other route
  // keeps BootstrapGate, then today's auth/onboarding routing.
  return (
    <BrowserRouter>
      <TrainIdentityProvider probe={recoveryProbe}>
        <Routes>
          <Route element={<ExecutionShellGate />}>
            <Route element={<MainLayout />}>
              <Route path="/train/*" element={<Train />} />
            </Route>
          </Route>
          <Route element={<BootstrapGate />}>
            <Route path="/auth" element={<AccountGateScreen />} />
            <Route path="/login" element={<WelcomeBackScreen />} />
            <Route path="/auth/email" element={<EmailEntryScreen />} />
            <Route path="/auth/email/verify" element={<OtpVerifyScreen />} />
            <Route path="/auth/return" element={<AuthReturnScreen />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route element={<OnboardingGate />}>
              <Route element={<MainLayout />}>
                <Route path="/" element={<HomeDashboard />} />
                <Route path="/program" element={<Program />} />
                <Route path="/program/all" element={<AllPrograms />} />
                <Route path="/program/cycles" element={<AllCycles />} />
                <Route path="/program/all/:programId" element={<ProgramDetails />} />
                <Route path="/program/cycles/:cycleId" element={<CycleProgramDetails />} />
                <Route path="/program/cycles/:cycleId/builder" element={<ManualBuilderMulti />} />
                <Route path="/program/cycles/:cycleId/builder/week/:weekNumber/workout/:orderIndex" element={<ManualWorkoutEditor />} />
                <Route path="/program/cycles/:cycleId/builder/workout/:workoutId" element={<ManualWorkoutEditor />} />
                <Route path="/program/manual-new" element={<ManualNewProgram />} />
                <Route path="/program/manual-builder" element={<ManualBuilder />} />
                <Route path="/program/manual-convert" element={<ManualConvert />} />
                <Route path="/program/manual-builder/workout/:workoutId" element={<ManualWorkoutEditor />} />
                <Route path="/program/manual-builder-multi" element={<ManualBuilderMulti />} />
                <Route path="/progress/*" element={<Progress />} />
                <Route path={getAIWeeklyPlanBuilderPath()} element={<AIWeeklyPlanBuilder />} />
                <Route path="/ai" element={<AICoach />} />
              </Route>
            </Route>
            <Route path="*" element={<UnknownRouteRedirect bootstrap={bootstrap} />} />
          </Route>
        </Routes>
      </TrainIdentityProvider>
    </BrowserRouter>
  );
}
